// https://ulap.biz/app/trans/ROGmJ
// PCASE Number 2025-07-0900


import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Divider, Grid, Chip, Avatar, MenuItem, IconButton, makeStyles, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, CardHeader, Select, InputBase, withStyles
} from '@material-ui/core';
import { Delete as DeleteIcon, Edit as EditIcon, Storage, Person, Group } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useRequest, useNotif } from 'src/hooks';
import SelectWithSearchV2 from 'src/components/SelectWithSearchV2';
import { makeStyles as muiMakeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

const useStyles = muiMakeStyles((theme) => ({
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
    fontFamily: "'Poppins', sans-serif",
  },
  dialogContent: {
    paddingTop: '8px !important',
    paddingBottom: '24px !important'
  },
  assignedToRoot: {
    background: '#fff',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    minHeight: 80,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  icon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    minHeight: 40,
  },
  noApprover: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '1em', 
    fontFamily: "'Poppins', sans-serif",
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  tableCell: {
    fontFamily: "'Poppins', sans-serif",
  },
  button: {
    fontFamily: "'Poppins', sans-serif",
  },
}));

const UserMenuItem = ({ value, description, user, ...props }) => (
    <MenuItem {...props}>
        <Box display="flex" alignItems="center" gridGap="1em">
        <Avatar src={user?.photoURL} />
        <Typography>{description}</Typography>
        </Box>
    </MenuItem>
);

const UserSelect = ({ label, value, onChange, options }) => (
  <SelectWithSearchV2
    label={label}
    value={value}
    onChange={e => onChange(e.target.value)}
    options={options}
    multiple
    CustomMenuItem={UserMenuItem}
    searchKeys={['description']}
  />
);

const EntitySelect = ({ label, value, onChange, options, ...otherProps }) => (
  <SelectWithSearchV2
    label={label}
    value={value ? [String(value)] : []}
    onChange={e => onChange(Array.isArray(e.target.value) ? (e.target.value[0] || '') : e.target.value)}
    options={options}
    multiple={false}
    searchKeys={['label', 'description']}
    {...otherProps}
  />
);

//for transaction names (per transaction)
const IXJCD_TO_NAME = {
  585: 'Official Business',
  586: 'Leave Application',
};

function groupSectionsByTransaction(kvs) {
  const grouped = {};
  Object.entries(kvs).forEach(([key, config]) => {
    if (Array.isArray(config.ixJCd)) {
      config.ixJCd.forEach(ixJCd => {
        if (!grouped[ixJCd]) grouped[ixJCd] = [];
        grouped[ixJCd].push({
          key,
          label: config.id1.label,
          type: key.includes('approver') ? 'Approver' : 'Reviewer',
          config
        });
      });
    }
  });
  return grouped;
}

const TRANSACTIONS = [
  { ixJCd: 585, name: 'Official Business' },
  { ixJCd: 586, name: 'Leave Application' },
  { ixJCd: 588, name: 'Overtime Application' }
];

// Custom styled input for orange underline
const OrangeInput = withStyles((theme) => ({
  input: {
    borderRadius: 6,
    position: 'relative',
    backgroundColor: '#fff',
    border: '2px solid #ff9800',
    fontSize: 18,
    padding: '10px 26px 10px 12px',
    fontWeight: 700,
    color: '#ff9800',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderColor: '#ff9800',
      boxShadow: '0 0 0 2px rgba(255,152,0,0.2)',
    },
  },
}))(InputBase);

const UserChoices = ({ onFetchingChange = () => {} }) => {
  const classes = useStyles();
  const req = useRequest();
  const notif = useNotif();
  const { kvs = {} } = useSelector(state => state.bizMeta);
  const { users: bizUsers = [] } = useSelector(state => state.bizMeta);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [mappings, setMappings] = useState({});
  const [entityOptions, setEntityOptions] = useState({});
  const [modal, setModal] = useState({ open: false, type: '', item: null, isEditing: false });
  const [selectedIxJCd, setSelectedIxJCd] = useState(TRANSACTIONS[0].ixJCd);

  const isMounted = useRef(true);

  // Dynamically generate approver config from KVS
  const getDynamicApproverConfig = useCallback(() => {
    const dynamicConfig = {};
    const dynamicSectionLabels = {};
    const dynamicEntityLabels = {};

    Object.entries(kvs).forEach(([key, config]) => {
      if (
        config?.ui_component === 'user_choices' &&
        config?.id1?.label &&
        typeof config?.id1?.ixSubType === 'number'
      ) {
        dynamicConfig[key] = key;
        dynamicSectionLabels[key] = config.id1.label + (key.includes('approver') ? ' Approvers' : ' Reviewers');
        dynamicEntityLabels[key] = config.id1.label;
      }
    });

    return { dynamicConfig, dynamicSectionLabels, dynamicEntityLabels };
  }, [kvs]);

  const { dynamicConfig, dynamicSectionLabels, dynamicEntityLabels } = getDynamicApproverConfig();
  const entityTypes = Object.keys(dynamicConfig);

  const fetchAllData = useCallback(async () => {
    setFetching(true);
    setLoading(true);
    try {
      const mappingKeys = Object.values(dynamicConfig);
      const mappingRes = await req.post('/setup/biz/kvs/items', { keys: mappingKeys });
      const newMappings = {};
      entityTypes.forEach(type => { newMappings[type] = []; });
      (mappingRes.data || []).forEach(item => {
        const type = Object.keys(dynamicConfig).find(t => dynamicConfig[t] === item.key.trim());
        let assignees = [];
        if (Array.isArray(item.value)) {
          assignees = item.value;
        } else if (typeof item.value === 'string') {
          try {
            const parsed = JSON.parse(item.value);
            assignees = Array.isArray(parsed) ? parsed : [item.value];
          } catch {
            assignees = [item.value];
          }
        }
        if (type && item.id1 && assignees.length > 0) {
          newMappings[type].push({
            entity: item.id1,
            assignees,
          });
        }
      });
      if (isMounted.current) setMappings(newMappings);

      // Caching logic for sub-entity options
      const newEntityOptions = { ...entityOptions };
      for (const type of entityTypes) {
        if (!newEntityOptions[type] || newEntityOptions[type].length === 0) {
        const kvsKey = dynamicConfig[type];
        const kvsSetting = kvs[kvsKey];
        if (
          kvsSetting &&
          kvsSetting.id1 &&
          typeof kvsSetting.id1.ixSubType === 'number' &&
          kvsSetting.id1.label
        ) {
          const subRes = await req.post('/lib/sub', { ixSubType: kvsSetting.id1.ixSubType });
          newEntityOptions[type] = (subRes.data?.sub_list || []).map(i => ({
            value: String(i.id || i.sub_id), label: i.name || i.sub_title, description: i.name || i.sub_title
          }));
        } else {
          newEntityOptions[type] = [];
          }
        }
      }
      if (isMounted.current) setEntityOptions(newEntityOptions);

      console.log('mappingRes.data:', mappingRes.data);
      console.log('newMappings:', newMappings);
    } catch (error) {
      notif.error('Failed to load settings data.');
    } finally {
      if (isMounted.current) setLoading(false);
      if (isMounted.current) setFetching(false);
    }
  }, [dynamicConfig, kvs, entityOptions]);

  useEffect(() => {
    isMounted.current = true;
    fetchAllData();
    return () => { isMounted.current = false; };
  }, [Object.keys(kvs).join(',')]);

  const handleOpenModal = (type, item = null) => {
    setModal({
      open: true,
      type,
      item: item || { entity: '', assignees: [] },
      isEditing: !!item,
    });
  };

  const handleCloseModal = () => {
    setModal({ open: false, type: '', item: null, isEditing: false });
  };

  const handleModalChange = (field, value) => {
    setModal(prev => ({ ...prev, item: { ...prev.item, [field]: value } }));
  };

  const handleSave = async () => {
    const { type, item, isEditing } = modal;
    const kvsKey = dynamicConfig[type];

    if (!item.entity || !item.assignees || item.assignees.length === 0) {
      return notif.error("Please fill all fields.");
    }

    let newEntry = { ...item };
    let currentEntries = mappings[type] || [];
    
    let updatedEntries = currentEntries.filter(
      e => !(String(e.entity) === String(item.entity))
    );
    
    updatedEntries.push(newEntry);

    console.log('--- handleSave ---');
    console.log('currentEntries:', currentEntries);
    console.log('updatedEntries:', updatedEntries);

    const endpoint = '/setup/biz/kvs/save';
    const payload = {
      [kvsKey]: {
        id1: item.entity,
        value: item.assignees,
      }
    };

    setLoading(true);
    try {
      await req.post(endpoint, payload);
      notif.success('Approver saved successfully.');
      handleCloseModal();
      await fetchAllData();
    } catch (error) {
      notif.error('Failed to save approver.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, entityId) => {
    const kvsKey = dynamicConfig[type];
    
    setLoading(true);
    try {
      await req.post('/setup/biz/kvs/delete', {
        [kvsKey]: { id1: entityId }
      });
      
      if (!isMounted.current) return;
      notif.success('Approver deleted successfully.');
      await fetchAllData();
    } catch (error) {
      if (!isMounted.current) return;
      console.error('Delete error:', error);
      notif.error('Failed to delete approver.');
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  };

  const userOptions = bizUsers.map(u => ({
    value: u.username,
    description: u.name || u.username,
    user: u
  }));

  const renderEntityName = (type, entityId) => {
    const options = entityOptions[type] || [];
    const option = options.find(opt => String(opt.value) === String(entityId));
    return option ? option.label : 'Unknown';
  };

  const renderEntityId = (entityId) => entityId;

  const renderAssigneeChips = (assignees) => {
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {assignees.map((assignee, index) => (
          <Chip
            key={index}
            label={assignee}
            size="small"
            style={{ 
              background: '#ff9800', 
              color: '#fff',
              fontSize: '0.75rem',
              height: '20px'
            }}
          />
        ))}
      </Box>
    );
  };

  const getSectionsForTransaction = (kvs, ixJCd) =>
    Object.entries(kvs)
      .filter(([key, config]) =>
        Array.isArray(config.ixJCd) && config.ixJCd.includes(ixJCd)
      )
      .map(([key, config]) => ({
        key,
        label: config.id1.label,
        type: key.includes('approver') ? 'Approver' : 'Reviewer'
      }));

  // Filter sections for the selected transaction
  const filteredSections = Object.entries(kvs)
    .filter(([key, config]) =>
      Array.isArray(config.ixJCd) && config.ixJCd.includes(selectedIxJCd)
    )
    .map(([key, config]) => ({
      key,
      label: config.id1.label,
      type: key.includes('approver') ? 'Approver' : 'Reviewer'
    }));


    //fix table UI alignment of columns 
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Set Up Approvers & Reviewers</Typography>
      <Select
        value={selectedIxJCd}
        onChange={e => setSelectedIxJCd(e.target.value)}
        input={<OrangeInput />}
        IconComponent={ArrowDropDownIcon}
        style={{ marginBottom: 24, minWidth: 250 }}
      >
        {TRANSACTIONS.map(tx => (
          <MenuItem key={tx.ixJCd} value={tx.ixJCd}>
            {tx.name}
          </MenuItem>
        ))}
      </Select>
       
       {/* fix the loading area for better ui */}
      {fetching ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={240}>
          <CircularProgress size={48} thickness={4.5} color="primary" />
          <Typography variant="subtitle1" color="textSecondary" style={{ marginTop: 16, fontFamily: "'Poppins', sans-serif" }}>
            Loading data...
          </Typography>
        </Box>
      ) : (
        filteredSections.map(section => (
          <Paper
            key={section.key}
            elevation={3}
            style={{
              marginBottom: 32,
              borderRadius: 16,
              padding: 24,
              background: '#fff'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                {section.type === 'Approver' ? (
                  <Person style={{ color: '#ff9800', marginRight: 8 }} />
                ) : (
                  <Group style={{ color: '#ff9800', marginRight: 8 }} />
                )}
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                  {section.label} {section.type}
                </Typography>
              </Box>
              <Button
                variant="contained"
                style={{
                  background: section.type === 'Approver' ? '#ff9800' : '#ff9800',
                  color: '#fff',
                  borderRadius: 8,
                  fontWeight: 600
                }}
                onClick={() => handleOpenModal(section.key)}
              >
                Add {section.type}
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ minWidth: 180, width: 200, fontWeight: 700 }}>{section.label}</TableCell>
                    <TableCell style={{ minWidth: 100, width: 120, fontWeight: 700 }}>ID</TableCell>
                    <TableCell style={{ minWidth: 250, fontWeight: 700 }}>Assignees</TableCell>
                    <TableCell style={{ width: 100, fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(mappings[section.key] || []).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ minWidth: 180, width: 200 }}>{renderEntityName(section.key, item.entity)}</TableCell>
                      <TableCell style={{ minWidth: 100, width: 120 }}>{renderEntityId(item.entity)}</TableCell>
                      <TableCell style={{ minWidth: 250 }}>
                        {Array.isArray(item.assignees) && item.assignees.length > 0
                          ? item.assignees.map((a, i) => (
                              <Chip
                                key={i}
                                label={a}
                                style={{
                                  background: '#ff9800',
                                  color: '#fff',
                                  marginRight: 4,
                                  fontWeight: 600
                                }}
                                size="small"
                              />
                            ))
                          : <span style={{ color: '#aaa', fontFamily: "'Poppins', sans-serif" }}>None</span>
                        }
                      </TableCell>
                      <TableCell style={{ width: 100 }}>
                        <IconButton size="small" onClick={() => handleOpenModal(section.key, item)}>
                          <EditIcon style={{ color: '#ff9800' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(section.key, item.entity)}>
                          <DeleteIcon style={{ color: '#ff9800' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}
      <Dialog open={modal.open} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle className={classes.typography}>
          {modal.isEditing ? 'Edit' : 'Add'} {modal.type ? (dynamicSectionLabels[modal.type]?.includes('Approvers') ? 'Approver' : 'Reviewer') : ''} for {modal.type ? dynamicEntityLabels[modal.type] : ''}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EntitySelect
                label={modal.type ? dynamicEntityLabels[modal.type] : ''}
                value={modal.item?.entity}
                onChange={val => handleModalChange('entity', val)}
                options={entityOptions[modal.type] || []}
                disabled={modal.isEditing}
              />
            </Grid>
            <Grid item xs={12}>
              <UserSelect
                label="Assignee(s)"
                value={modal.item?.assignees || []}
                onChange={val => handleModalChange('assignees', val)}
                options={userOptions}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary" disabled={loading} className={classes.button}>Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={loading} className={classes.button}>
            {loading ? 'Saving...' : (modal.isEditing ? 'Save Changes' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserChoices;
